import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { NextPage } from 'next';
import Link from 'next/link'

import cName from "classnames";
import styles from './index.module.scss';

import { Form, Input, Toast } from '@douyinfe/semi-ui';

import { toArray } from 'lodash';
import { getServerSideProps, getHead } from '@/utils/useHead';
import { arrayBufferToBase64 } from '@/utils/index';
import request from '@/utils/request';

import { useRouter } from 'next/router';
import { useTranslations } from "next-intl";

interface IProps {
}

export { getServerSideProps }

const Apply: NextPage<IProps> = ({ menuDetail }: any) => {
  const router = useRouter()
  const t = useTranslations("exhibition.apply");

  const transOption = useCallback((list: any = []) => {
    return list?.map((item: any) => {
      return {
        ...item,
        key: item.id,
        value: item.id,
        label: router.locale == 'en' ? item.name_en : item.name_cn
      }
    })
  }, [])
  const transTree = useCallback((list: any = []) => {
    return list?.map((item: any) => {
      return {
        ...item,
        key: String(item.id),
        value: item.id,
        label: router.locale == 'en' ? item.name_en : item.name_cn,
        children: item.childlist?.length > 0 ? transTree(item.childlist) : []
      }
    })
  }, [])

  const [country_list, setCountryList] = useState([])
  const getCountryList = useCallback(async () => {
    const { data } = await request.get('/api/get_country')
    setCountryList(transOption(data?.data))
  }, [])

  const [category_list, setCategoryList] = useState([])
  const getCategoryList = useCallback(async () => {
    const { data } = await request.get('/api/product_category_list')
    setCategoryList(transTree(data?.data))
  }, [])

  const [verify_img, setVerifyImg] = useState("")
  const [verify_cookie, setVerifyCookie] = useState("")
  const [verify_code, setVerifyCode] = useState("")
  const getVerify = useCallback(async () => {
    const { data } = await request.get(`/api/get_verify`)
    setVerifyImg('data:image/png;base64,' + arrayBufferToBase64(data?.data?.img?.data))
    setVerifyCookie(data?.data?.['cbce-verify-cookie'])
  }, [])

  const [province_list, setProvinceList] = useState([])
  const [city_list, setCityList] = useState([])
  const [regionVisible, setRegionVisible] = useState(false)
  const getRegion = useCallback(async (pid: any) => {
    const { data } = await request.get(`/api/get_region`, {
      params: {
        pid
      }
    })
    const _list = transOption(toArray(data?.data))
    !!pid ? setCityList(_list) : setProvinceList(_list)
  }, [])

  const formRef = useRef<any>()
  const renderLabel = ({
    className,
    onExpand,
    data,
    onCheck,
    checkStatus,
    expandIcon,
  }: any) => {
    const { label } = data;
    const isLeaf = !(data.children && data.children.length);
    return (
      <li
        className={className}
        role="treeitem"
        onClick={isLeaf ? onCheck : onExpand}
      >
        {isLeaf ? null : expandIcon}
        {
          isLeaf
            ? <div tabIndex={0} aria-checked={checkStatus.checked} onClick={onCheck}></div>
            : null
        }
        <span style={{ marginLeft: isLeaf ? '20px' : '' }}>{label}</span>
      </li>
    );
  };

  const submit = useCallback(() => {
    formRef.current.validate().then(async () => {
      if (formRef.current.getValue('booth_type') == '1' && formRef.current.getValue('booth_input1') < 18) {
        return Toast.warning({
          content: t('section2.item14mes1'),
          duration: 3,
        })
      }
      if (formRef.current.getValue('booth_type') == '2' && formRef.current.getValue('booth_input2') < 9) {
        return Toast.warning({
          content: t('section2.item14mes2'),
          duration: 3,
        })
      }
      if (!verify_code) {
        return Toast.warning({
          content: t('section2.item13mes1'),
          duration: 3,
        })
      }
      const { data } = await request.post('/api/exhibitor_apply', {
        lang: router.locale,
        captcha: verify_code,
        ...formRef.current.getFormState().values
      }, {
        headers: {
          'cbce-verify-cookie': verify_cookie,
        }
      })
      if (data.code == 1) {
        Toast.success({
          content: t('success'),
          duration: 3,
        })
        formRef.current.reset()
      } else {
        getVerify()
        Toast.error({
          content: data.msg,
          duration: 3,
        })
      }
    }).catch((err: any) => {
      Toast.warning({
        content: t('failed'),
        duration: 3,
      })
    })
  }, [verify_code, verify_cookie])

  useEffect(() => {
    getCountryList();
    getCategoryList();
    getVerify();
    getRegion(0);
  }, [])

  return (
    <div>
      <div className={styles.container}>
        {getHead(menuDetail)}
        <div className={styles.content}>
          <img className={styles.banner} src="https://img.cbcechina.com/2026-04-02/fa889ced9c0cccc9013421c9b5e66890.jpg" alt="" />
          <div className={styles.form}>
            <div className={styles.head}>
              <h2 className={styles.title}>{t('title')}<span>{t('text')}</span></h2>
              <div className={styles.btns}>
                <Link href="/">
                  <a className={cName(styles.btn, styles.red)}>{t('btn1')}</a>
                </Link>
                <Link href={t('btn2link')}>
                  <a className={cName(styles.btn)} target='_blank'>{t('btn2')}</a>
                </Link>
              </div>
            </div>
            <Form
              getFormApi={(formApi: any) => formRef.current = formApi}
              labelPosition="left"
              labelWidth={router.locale == 'en' ? '160px' : '108px'}
              labelAlign="right"
            >
              {
                (({ formState, values, formApi }) => (
                  <div>
                    <h3><span>*</span>{t('section1.title')}</h3>
                    <Form.RadioGroup
                      className={styles.type}
                      field="booth_type"
                      noLabel
                      direction='vertical'
                      rules={[
                        { required: true, message: t('section1.message') },
                      ]}
                      onChange={() => {
                        formRef.current.reset(['booth_input1', 'booth_input2'])
                      }}
                    >
                      <Form.Radio value="1">
                        <div className={styles.item}>
                          <div style={{ width: router.locale == 'en' ? '116px' : '65px' }}>
                            <p className={styles.tit}>{t('section1.item1tit')}</p>
                            <p className={styles.txt}>{t('section1.item1txt')}</p>
                          </div>
                          <Form.InputNumber
                            field="booth_input1"
                            noLabel
                            placeholder={t('section1.item1tip')}
                            addonAfter={t('section1.item1sub')}
                            disabled={formRef.current.getValue('booth_type') != '1'}
                            hideButtons
                            trigger='blur'
                            rules={[
                              { required: formRef.current.getValue('booth_type') == '1', message: t('section1.item1mes') },
                            ]}
                          />
                        </div>
                      </Form.Radio>
                      <Form.Radio value="2">
                        <div className={styles.item}>
                          <div style={{ width: router.locale == 'en' ? '116px' : '65px' }}>
                            <p className={styles.tit}>{t('section1.item2tit')}</p>
                            <p className={styles.txt}>{t('section1.item2txt')}</p>
                          </div>
                          <Form.InputNumber
                            field="booth_input2"
                            noLabel
                            placeholder={t('section1.item2tip')}
                            addonAfter={t('section1.item2sub')}
                            disabled={formRef.current.getValue('booth_type') != '2'}
                            hideButtons
                            trigger='blur'
                            rules={[
                              { required: formRef.current.getValue('booth_type') == '2', message: t('section1.item2mes') },
                            ]}
                          />
                        </div>
                      </Form.Radio>
                    </Form.RadioGroup>
                    <h3><span>*</span>{t('section2.title')}</h3>
                    <Form.Input
                      field={router.locale == 'en' ? 'company_en' : 'company_cn'}
                      label={t('section2.item1tit')}
                      placeholder={t('section2.item1tip')}
                      trigger='blur'
                      rules={[
                        { required: true, message: t('section2.item1mes1') },
                        { validator: (rule, value) => !!value, message: t('section2.item1mes2') }
                      ]}
                    />
                    <Form.Input
                      field="truename"
                      label={t('section2.item2tit')}
                      placeholder={t('section2.item2tip')}
                      trigger='blur'
                      rules={[
                        { required: true, message: t('section2.item2mes1') },
                        { validator: (rule, value) => !!value, message: t('section2.item2mes2') }
                      ]}
                    />
                    <Form.InputNumber
                      field="mobile"
                      label={t('section2.item3tit')}
                      hideButtons
                      placeholder={t('section2.item3tip')}
                      trigger='blur'
                      rules={[
                        { required: true, message: t('section2.item3mes1') },
                        { validator: (rule, value) => !!value, message: t('section2.item3mes2') }
                      ]}
                    />
                    <Form.RadioGroup
                      className={styles.gender}
                      field="gender"
                      label={t('section2.item4tit')}
                    >
                      <Form.Radio value="1">{t('section2.item4val1')}</Form.Radio>
                      <Form.Radio value="2">{t('section2.item4val2')}</Form.Radio>
                    </Form.RadioGroup>
                    <Form.Input
                      field="position"
                      label={t('section2.item5tit')}
                      placeholder={t('section2.item5tip')}
                      trigger='blur'
                    />
                    <Form.Select
                      field="country_id"
                      label={t('section2.item6tit')}
                      placeholder={t('section2.item6tip')}
                      onSelect={(value: any, option: any) => {
                        formRef.current.setValue('phone_code', '+' + option.code)
                        setRegionVisible(value == 256)
                      }}
                      rules={[
                        { required: true, message: t('section2.item6mes1') },
                      ]}
                      optionList={country_list}
                    >
                    </Form.Select>
                    {regionVisible && (
                      <Form.Slot label={{ text: t('section2.item7tit'), required: true }}>
                        <div className={styles.grid_2}>
                          <Form.Select
                            field="province_id"
                            noLabel
                            placeholder={t('section2.item7tip1')}
                            rules={[
                              { required: true, message: t('section2.item7mes1') },
                            ]}
                            optionList={province_list}
                            onSelect={(value: any, option: any) => {
                              getRegion(value)
                              formRef.current.reset(['city_id'])
                            }}
                          >
                          </Form.Select>
                          <Form.Select
                            field="city_id"
                            noLabel
                            placeholder={t('section2.item7tip2')}
                            rules={[
                              { required: true, message: t('section2.item7mes2') },
                            ]}
                            optionList={city_list}
                            onSelect={(value: any, option: any) => {
                              formRef.current.setValue('phone_zone', option.area_code)
                            }}
                          >
                          </Form.Select>
                        </div>
                      </Form.Slot>
                    )}
                    <Form.Slot label={{ text: t('section2.item8tit') }}>
                      <div className={styles.grid_3}>
                        <Form.Input
                          className={styles.input}
                          field="phone_code"
                          noLabel
                          readonly
                          initValue={'+86'}
                        />
                        <Form.Input
                          className={styles.input}
                          field="phone_zone"
                          placeholder={t('section2.item8tip1')}
                          noLabel
                        />
                        <Form.InputNumber
                          className={styles.input}
                          hideButtons
                          field="phone"
                          placeholder={t('section2.item8tip2')}
                          noLabel
                        />
                      </div>
                    </Form.Slot>
                    <Form.Input
                      field="email"
                      label={t('section2.item9tit')}
                      placeholder={t('section2.item9tip')}
                      trigger='blur'
                      rules={[
                        { required: true, message: t('section2.item9mes1') },
                        { validator: (rule, value) => !!value, message: t('section2.item9mes2') }
                      ]}
                    />
                    <Form.Input
                      field="website"
                      label={t('section2.item10tit')}
                      placeholder={t('section2.item10tip')}
                    />
                    <div className={styles.grid_2}>
                      <Form.Input
                        field="company_type"
                        label={t('section2.item11tit')}
                        placeholder={t('section2.item11tip')}
                      />
                      <Form.TreeSelect
                        field="category_data"
                        label={t('section2.item12tit')}
                        labelWidth={router.locale == 'en' ? '160px' : '108px'}
                        dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                        treeData={category_list}
                        placeholder={t('section2.item12tip')}
                        disableStrictly
                        leafOnly
                        renderFullLabel={renderLabel}
                        rules={[
                          { required: true, message: t('section2.item12mes1') },
                        ]}
                      />
                    </div>
                  </div>
                ))
              }
            </Form>
            <Input className={styles.verify} placeholder={t('section2.item13tip')} addonAfter={<img className={styles.code} src={verify_img} alt="" onClick={() => getVerify()} />} value={verify_code} onChange={(e: any) => setVerifyCode(e)} />
            <div className={styles.button} onClick={submit}>{t('btn3')}</div>
          </div>
        </div>
        <p className={styles.copyright}>{t('copyright')}</p>
      </div>
    </div>
  );
};

export default Apply;
